/**
 * Course Creation Form component for trAInedup.ai
 * Allows admins and instructors to create new courses with modules
 */

import React, { useState, useEffect } from 'react';
import { FileUpload } from '../common/FileUpload';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save,
  X,
} from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Card } from '../common/Card';
import { ModuleCreationForm } from './ModuleCreationForm';
import { ModuleList } from './ModuleList';
import { useAuth } from '../../contexts/AuthContext';
import { courseService } from '../../services/firebase/courses';
import { ROUTES } from '../../utils/constants';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Course,
  CourseCategory,
  CourseDifficulty,
  COURSE_CATEGORIES,
  DEFAULT_COURSE_CATEGORY,
} from '../../types/course';
import { Timestamp } from 'firebase/firestore';

// Locally extend Module type to include isNew
type ModuleWithIsNew = import('../../types/course').Module & { isNew?: boolean };

interface CourseFormData {
  title: string;
  description: string;
  category: CourseCategory;
  difficulty: CourseDifficulty;
  estimatedDuration: number;
  tags: string[];
  thumbnail: string;
  requiredSubscriptionTier: string;
  isFree: boolean;
}

/**
 * Course creation form component
 */
export const CourseCreationForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: urlCourseId } = useParams<{ id: string }>();
  
  // Determine if we're in edit mode
  const isEditMode = !!urlCourseId;
  
  // Use existing course ID for edit mode, generate new one for create mode
  const [courseId] = useState(() => {
    if (isEditMode && urlCourseId) {
      return urlCourseId;
    }
    // Generate a unique course ID using timestamp and random string for new courses
    return `course_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });
  
  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    description: '',
  category: DEFAULT_COURSE_CATEGORY,
    difficulty: CourseDifficulty.BEGINNER,
    estimatedDuration: 60,
    tags: [],
    thumbnail: '',
    requiredSubscriptionTier: 'free',
    isFree: true,
  });

  const [modules, setModules] = useState<ModuleWithIsNew[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEditMode);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [editingModule, setEditingModule] = useState<ModuleWithIsNew | undefined>();
  const [insertPosition, setInsertPosition] = useState<number | null>(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailTempPath, setThumbnailTempPath] = useState<string | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  /**
   * Load existing course data for edit mode
   */
  useEffect(() => {
    const loadCourseData = async () => {
      if (!isEditMode || !courseId) return;
      
      try {
        setIsLoadingData(true);
        setError('');
        
        // Load course data
        const course = await courseService.getCourse(courseId);
        if (!course) {
          setError('Course not found');
          return;
        }
        
        // Populate form data
        setFormData({
          title: course.title,
          description: course.description,
          category: course.category as CourseCategory,
          difficulty: course.difficulty as CourseDifficulty,
          estimatedDuration: course.estimatedDuration,
          tags: course.tags || [],
          thumbnail: course.thumbnail || '',
          requiredSubscriptionTier: course.requiredSubscriptionTier || 'free',
          isFree: course.isFree,
        });
        
        // Load course modules
        const courseModules = await courseService.getCourseModules(courseId);
        setModules(courseModules);
        
      } catch (err: any) {
        setError(err.message || 'Failed to load course data');
      } finally {
        setIsLoadingData(false);
      }
    };
    
    loadCourseData();
  }, [isEditMode, courseId]);

  /**
   * Handle form input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Synchronize isFree with requiredSubscriptionTier to prevent conflicts
    if (name === 'requiredSubscriptionTier') {
      setFormData(prev => ({
        ...prev,
        requiredSubscriptionTier: value,
        isFree: value === 'free', // Automatically set isFree based on tier
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
      }));
    }
  };

  /**
   * Handle thumbnail file upload
   */
  const handleThumbnailUpload = async (file: File) => {
    if (!user?.uid) return;
    setThumbnailUploading(true);
    try {
      setThumbnailFile(file);
      const storage = getStorage();
      const tempPath = `courses/new-course/thumbnails/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, tempPath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, thumbnail: url }));
      setThumbnailTempPath(tempPath);
    } catch (err) {
      setError('Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  /**
   * Add tag to the list
   */
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  /**
   * Remove tag from the list
   */
  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  /**
   * Handle module save
   */
  const handleModuleSave = async (moduleData: any) => {
    // Generate module ID upfront for consistent file paths
    const moduleId = editingModule?.id || `module_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Determine the order for the new module
    let moduleOrder = moduleData.order;
    if (insertPosition !== null) {
      moduleOrder = insertPosition;
    } else if (!editingModule) {
      // New module without specific position - add to end
      moduleOrder = modules.length;
    }
    
    // Use the content directly from moduleData as it's already properly structured
    // by the ModuleCreationForm
    // Mark as new if not editing an existing module
    const isActuallyNew = !editingModule;
    const newModule: Omit<ModuleWithIsNew, 'id'> & { id: string; isNew?: boolean } = {
      id: moduleId,
      courseId: courseId,
      title: moduleData.title,
      description: moduleData.description,
      type: moduleData.type,
      estimatedDuration: moduleData.estimatedDuration,
      content: moduleData.content,
      isRequired: true,
      order: moduleOrder,
      createdAt: editingModule?.createdAt || Timestamp.now() as any,
      updatedAt: Timestamp.now() as any,
      isNew: isActuallyNew,
    };

    if (editingModule) {
      // Update existing module
      setModules(prev => prev.map(m => m.id === editingModule.id ? newModule : m));
    } else {
      // Add new module
      if (insertPosition !== null) {
        // Insert at specific position and reorder subsequent modules
        const newModules = [...modules];
        newModules.splice(insertPosition, 0, newModule);
        // Update order for all modules
        const reorderedModules = newModules.map((m, index) => ({
          ...m,
          order: index,
        }));
        setModules(reorderedModules);
      } else {
        // Add to end
        setModules(prev => [...prev, newModule]);
      }
    }

    setShowModuleForm(false);
    setEditingModule(undefined);
    setInsertPosition(null);
  };

  /**
   * Handle module edit
   */
  const handleModuleEdit = (module: ModuleWithIsNew) => {
    setEditingModule(module);
    setShowModuleForm(true);
  };

  /**
   * Handle module delete
   */
  const handleModuleDelete = (moduleId: string) => {
    const newModules = modules.filter(m => m.id !== moduleId);
    // Reorder remaining modules
    const reorderedModules = newModules.map((m, index) => ({
      ...m,
      order: index,
    }));
    setModules(reorderedModules);
  };

  /**
   * Handle modules reorder
   */
  const handleModulesReorder = async (newModules: ModuleWithIsNew[]) => {
    setModules(newModules);
    
    // If we're in edit mode, save the reordering to the database
    if (isEditMode) {
      try {
        const moduleIds = newModules.map(m => m.id);
        await courseService.reorderModules(courseId, moduleIds);
      } catch (error) {
        console.error('Failed to save module order:', error);
        setError('Failed to save module order');
      }
    }
  };

  /**
   * Handle add module
   */
  const handleAddModule = () => {
    setInsertPosition(null);
    setEditingModule(undefined);
    setShowModuleForm(true);
  };

  /**
   * Handle insert module at position
   */
  const handleInsertModule = (position: number) => {
    setInsertPosition(position);
    setEditingModule(undefined);
    setShowModuleForm(true);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.uid) {
      setError('You must be logged in to create a course');
      return;
    }

    if (modules.length === 0) {
      setError('Please add at least one module to the course');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isEditMode) {
        // Update existing course
        const courseUpdates = {
          title: formData.title,
          description: formData.description,
          shortDescription: formData.description.substring(0, 100) + '...',
          category: formData.category,
          difficulty: formData.difficulty,
          estimatedDuration: formData.estimatedDuration,
          tags: formData.tags,
          moduleIds: modules.map(m => m.id),
          requiredSubscriptionTier: formData.requiredSubscriptionTier,
          isFree: formData.isFree,
          updatedAt: Timestamp.now(),
        };

        // Handle thumbnail update
        let finalThumbnailUrl = formData.thumbnail;
        if (thumbnailFile && thumbnailTempPath) {
          const storage = getStorage();
          const tempRef = ref(storage, thumbnailTempPath);
          const fileName = thumbnailTempPath.split('/').pop() || `thumbnail_${Date.now()}`;
          const finalPath = `courses/${courseId}/thumbnails/en/${fileName}`;
          const finalRef = ref(storage, finalPath);
          await uploadBytes(finalRef, thumbnailFile);
          finalThumbnailUrl = await getDownloadURL(finalRef);
          // Delete temp file
          await deleteObject(tempRef);
        }

        // Update course with thumbnail
        await courseService.updateCourse(courseId, {
          ...courseUpdates,
          thumbnail: finalThumbnailUrl,
          coverImage: finalThumbnailUrl,
        });

        // Save modules: create new ones, update existing ones
        for (const module of modules) {
          if (module.isNew) {
            const moduleData = (({ id: _id, isNew: _isNew, ...rest }) => rest)(module);
            await courseService.createModule({
              ...moduleData,
              courseId: courseId,
            });
          } else {
            await courseService.updateModule(module.id, {
              ...module,
              courseId: courseId,
              updatedAt: Timestamp.now(),
            });
          }
        }
      } else {
        // Create new course
        const courseData: Course = {
          id: courseId,
          title: formData.title,
          description: formData.description,
          shortDescription: formData.description.substring(0, 100) + '...',
          category: formData.category,
          difficulty: formData.difficulty,
          estimatedDuration: formData.estimatedDuration,
          tags: formData.tags,
          thumbnail: '', // will update after moving file
          coverImage: '',
          moduleIds: modules.map(m => m.id), // Use pre-generated module IDs
          requiredSubscriptionTier: formData.requiredSubscriptionTier,
          isFree: formData.isFree,
          totalModules: modules.length,
          enrollmentCount: 0,
          averageRating: 0,
          reviewCount: 0,
          isPublished: false,
          createdBy: user.uid,
          createdAt: Timestamp.now() as any,
          updatedAt: Timestamp.now(),
        };
        
        // Create course with pre-generated ID
        await courseService.setCourse(courseId, courseData);

        // Move thumbnail to permanent location if uploaded
        let finalThumbnailUrl = '';
        if (thumbnailFile && thumbnailTempPath) {
          const storage = getStorage();
          const tempRef = ref(storage, thumbnailTempPath);
          const fileName = thumbnailTempPath.split('/').pop() || `thumbnail_${Date.now()}`;
          const finalPath = `courses/${courseId}/thumbnails/en/${fileName}`;
          const finalRef = ref(storage, finalPath);
          await uploadBytes(finalRef, thumbnailFile);
          finalThumbnailUrl = await getDownloadURL(finalRef);
          // Update course with thumbnail
          await courseService.updateCourse(courseId, {
            thumbnail: finalThumbnailUrl,
            coverImage: finalThumbnailUrl,
          });
          // Delete temp file
          await deleteObject(tempRef);
        }

        // Save modules using courseService
        for (const module of modules) {
          if (module.isNew) {
            // Remove id and isNew before passing to createModule
            const moduleData = (({ id: _id, isNew: _isNew, ...rest }) => rest)(module);
            await courseService.createModule({
              ...moduleData,
              courseId: courseId,
            });
          } else {
            await courseService.updateModule(module.id, {
              ...module,
              courseId: courseId,
              updatedAt: Timestamp.now(),
            });
          }
        }
      }

  // Navigate back to course management
  navigate(ROUTES.adminCourses);
    } catch (error: any) {
      setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} course`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle form submission
   */
  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Course Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="Enter course title"
          required
        />

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Category
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            {COURSE_CATEGORIES.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Difficulty Level
          </label>
          <select
            name="difficulty"
            value={formData.difficulty}
            onChange={handleInputChange}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            {Object.values(CourseDifficulty).map(difficulty => (
              <option key={difficulty} value={difficulty}>
                {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="Estimated Duration (minutes)"
          name="estimatedDuration"
          type="number"
          min="1"
          value={formData.estimatedDuration}
          onChange={handleInputChange}
          placeholder="e.g., 120"
          required
        />

        <FileUpload
          label="Course Thumbnail"
          onFileUpload={handleThumbnailUpload}
          accept="image/*"
          disabled={thumbnailUploading}
        />
        {formData.thumbnail && (
          <div className="mt-2">
            <img src={formData.thumbnail} alt="Thumbnail preview" className="h-24 rounded-lg border" />
          </div>
        )}
        {thumbnailUploading && (
          <div className="text-sm text-neutral-600 mt-2">Uploading...</div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Subscription Requirement
          </label>
          <select
            name="requiredSubscriptionTier"
            value={formData.requiredSubscriptionTier}
            onChange={handleInputChange}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="free">Free</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="corporate">Corporate</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Course Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={6}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Detailed course description"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Tags
        </label>
        <div className="flex space-x-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Add a tag"
          />
          <Button type="button" variant="outline" onClick={addTag}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-2 text-primary-600 hover:text-primary-800"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  /**
   * Render step 2: Course Modules
   */
  const renderModules = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-neutral-900">Course Modules</h3>
          <p className="text-neutral-600 text-sm">
            Drag and drop to reorder modules, or use the insert buttons to add modules at specific positions
          </p>
        </div>
      </div>

      <ModuleList
        modules={modules.sort((a, b) => a.order - b.order)}
        onModulesReorder={handleModulesReorder}
        onModuleEdit={handleModuleEdit}
        onModuleDelete={handleModuleDelete}
        onAddModule={handleAddModule}
        onInsertModule={handleInsertModule}
        isLoading={isLoadingData}
      />
    </div>
  );

  // Show module creation form
  if (showModuleForm) {
    // Generate moduleId for new modules or use existing one for editing
    const moduleId = editingModule?.id || `module_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return (
      <ModuleCreationForm
        courseId={courseId} // Use the upfront generated course ID
        moduleId={moduleId} // Pass the module ID for correct file paths
        existingModules={modules}
        onSave={handleModuleSave}
        onCancel={() => {
          setShowModuleForm(false);
          setEditingModule(undefined);
          setInsertPosition(null);
        }}
        editingModule={editingModule}
        insertPosition={insertPosition}
      />
    );
  }

  const stepTitles = ['Basic Information', 'Course Modules'];
  const totalSteps = stepTitles.length;

  // Show loading state while fetching data in edit mode
  if (isLoadingData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading course data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="font-montserrat font-bold text-3xl text-neutral-800 mb-2">
          {isEditMode ? 'Edit Course' : 'Create New Course'}
        </h1>
        <p className="text-lg text-neutral-600">
          {isEditMode 
            ? 'Update your course content and modules' 
            : 'Create a new course with modules for your students'
          }
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {stepTitles.map((title, index) => (
            <div
              key={index}
              className={`flex items-center ${index < stepTitles.length - 1 ? 'flex-1' : ''}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index + 1 <= currentStep
                    ? 'bg-primary text-white'
                    : 'bg-neutral-200 text-neutral-600'
                }`}
              >
                {index + 1}
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  index + 1 <= currentStep ? 'text-primary' : 'text-neutral-600'
                }`}
              >
                {title}
              </span>
              {index < stepTitles.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${
                    index + 1 < currentStep ? 'bg-primary' : 'bg-neutral-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-8 mb-6">
          {currentStep === 1 && renderBasicInfo()}
          {currentStep === 2 && renderModules()}
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-4">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentStep(prev => prev - 1);
                }}
              >
                Previous
              </Button>
            )}
            
            <Button
              type="button"
              variant="ghost"
              icon={X}
              onClick={() => navigate(ROUTES.adminCourses)}
            >
              Cancel
            </Button>
          </div>

          <div className="flex space-x-4">
            {currentStep < totalSteps ? (
              <Button
                type="button"
                variant="primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentStep(prev => prev + 1);
                }}
                disabled={currentStep === 1 && !formData.title.trim() && !isLoadingData}
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                variant="primary"
                icon={Save}
                loading={isLoading}
                disabled={isLoading || modules.length === 0}
              >
                {isLoading 
                  ? (isEditMode ? 'Updating Course...' : 'Creating Course...') 
                  : (isEditMode ? 'Update Course' : 'Create Course')
                }
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

